import React, { type KeyboardEvent, type MouseEvent, useCallback } from 'react';
import { styled } from '@compiled/react';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	Link as RouterLink,
	type LinkProps as RouterLinkProps,
} from '@atlassian/react-resource-router';

import { fg } from '@atlassian/jira-feature-gating';
import { useIsEmbedMode } from '@atlassian/jira-issue-view-embed-mode/src/index.tsx';

type LinkProps = RouterLinkProps & {
	wordBreak?: boolean;
	testId?: string;
	shouldDisplayStrikethrough?: boolean;
};

const Link = ({
	onClick,
	wordBreak = true,
	shouldDisplayStrikethrough = false,
	...rest
}: LinkProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isEmbedMode = fg('jira_nin_smart_link')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useIsEmbedMode()
		: false;
	const onClickWithAnalytics = useCallback(
		(e: MouseEvent | KeyboardEvent) => {
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'link',
				}),
				'listWithPopup',
			);
			onClick && onClick(e);
		},
		[createAnalyticsEvent, onClick],
	);
	return (
		<StyledLink
			wordBreak={wordBreak}
			shouldDisplayStrikethrough={shouldDisplayStrikethrough}
			{...rest}
			onClick={onClickWithAnalytics}
			{...(isEmbedMode && { target: '_blank', rel: 'noopener' })}
		/>
	);
};

// Fix for Compiled bug where invalid props are passed on to the underlying DOM element when using inheritance.
const PropFilteredLink = ({ wordBreak, shouldDisplayStrikethrough, ...rest }: LinkProps) => (
	<RouterLink {...rest} />
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledLink = styled(PropFilteredLink)<{
	wordBreak?: boolean;
	shouldDisplayStrikethrough?: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	wordBreak: (props) => (props.wordBreak ? 'break-word' : undefined),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	textDecoration: (props) =>
		props.shouldDisplayStrikethrough ? 'line-through !important' : undefined,
});

export default Link;
